import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Collapse, IconButton, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getStatusColor, hexToRgba } from '../../dashboard/calendar-view/calendarColorUtils';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import AppTooltip from '../../../shared/AppTooltip';
import { getStatusLabel } from '../../../shared/utils/status';
import './DayPlanTable.scss';

const formatDateStr = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DayPlanTable = ({ title, projects = [], emptyMessage, isToday }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [resizeKey, setResizeKey] = useState(0);

  useEffect(() => {
    let timeoutId = null;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setResizeKey(prev => prev + 1);
      }, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box className={`day-plan-section ${isToday ? 'today-section' : 'yesterday-section'}`}>
      <Box className="section-header" onClick={() => setExpanded(!expanded)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" className="section-title">
            {title}
          </Typography>
          <Tooltip title={t('dailyStandup.teamDetail.datesInfoTooltip')} arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, cursor: 'pointer' }} />
          </Tooltip>
        </Box>
        <IconButton size="small" className="expand-icon">
          {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box className="day-plan-content">
          {(!projects || projects.length === 0) ? (
            <Box className="empty-card-state">
              <InfoOutlinedIcon className="empty-icon" />
              <Typography className="empty-text">{emptyMessage}</Typography>
            </Box>
          ) : (
            projects.map((project, pIndex) => (
              <Box key={project.project_id || pIndex} className="project-group-container">
                <Box className="project-group-header">
                  <Typography className="project-name">
                    {project.project_name || 'Unassigned Project'}
                  </Typography>
                </Box>
                <Box className="project-group-content">
                  {(project.tickets || []).map((ticket, index) => {
                    const statusLabel = getStatusLabel(ticket.status);
                    const colorHex = ticket.status?.colour || ticket.status?.color || getStatusColor(statusLabel);
                    const statusInfo = typeof ticket.status === 'object' && ticket.status !== null ? ticket.status.info : null;
                    const hasOverdueInfo = statusInfo && (statusInfo.original_due_date || statusInfo.completion_date);

                    const overdueStatusTooltip = hasOverdueInfo ? (
                      <Box className="overdue-tooltip-content">
                        <Typography variant="caption" className="tooltip-heading">
                          Ticket was overdue when completed
                        </Typography>
                        {statusInfo.original_due_date && (
                          <Typography variant="caption" className="tooltip-detail-text">
                            Original Due Date: {formatDateStr(statusInfo.original_due_date)}
                          </Typography>
                        )}
                        {statusInfo.completion_date && (
                          <Typography variant="caption" className="tooltip-detail-text">
                            Completion Date: {formatDateStr(statusInfo.completion_date)}
                          </Typography>
                        )}
                      </Box>
                    ) : null;

                    const overdueTitleTooltip = hasOverdueInfo ? (
                      <Box className="overdue-tooltip-content">
                        <Typography variant="caption" className="tooltip-heading" sx={{ mb: 0.5 }}>
                          {ticket.title?.trim()}
                        </Typography>
                        <Typography variant="caption" className="tooltip-heading overdue-subheading">
                          Ticket was overdue when completed
                        </Typography>
                        {statusInfo.original_due_date && (
                          <Typography variant="caption" className="tooltip-detail-text">
                            Original Due Date: {formatDateStr(statusInfo.original_due_date)}
                          </Typography>
                        )}
                        {statusInfo.completion_date && (
                          <Typography variant="caption" className="tooltip-detail-text">
                            Completion Date: {formatDateStr(statusInfo.completion_date)}
                          </Typography>
                        )}
                      </Box>
                    ) : null;

                    return (
                      <Box
                        key={`${ticket.ticket_id || ticket.ticket_key}-${index}`}
                        className={`day-plan-row ${isToday ? 'today-plan-row' : 'yesterday-plan-row'}`}
                        style={{
                          '--status-color': colorHex,
                          '--status-bg': hexToRgba(colorHex, 0.1)
                        }}
                        onClick={() => window.open(`/task/${ticket.ticket_key}`, '_blank', 'noopener,noreferrer')}
                      >
                        <Box className="ticket-info">
                          <Typography className="ticket-key">
                            {ticket.ticket_key}
                          </Typography>
                          {hasOverdueInfo ? (
                            <AppTooltip
                              title={overdueTitleTooltip}
                              placement="top"
                              arrow
                              slotProps={{ tooltip: { className: 'app-tooltip' } }}
                            >
                              <Typography className="ticket-title">
                                {ticket.title?.trim()}
                              </Typography>
                            </AppTooltip>
                          ) : (
                            <OverflowTooltip
                              key={`tooltip-${resizeKey}-${ticket.ticket_id || ticket.ticket_key}-${index}`}
                              title={ticket.title?.trim()}
                              placement="top"
                              className="ticket-title"
                              tooltipWidth={500}
                            >
                              {ticket.title?.trim()}
                            </OverflowTooltip>
                          )}
                        </Box>

                        {hasOverdueInfo ? (
                          <AppTooltip
                            title={overdueStatusTooltip}
                            placement="top"
                            arrow
                            slotProps={{ tooltip: { className: 'app-tooltip' } }}
                          >
                            <Box className="status-badge" sx={{ maxWidth: '100px' }}>
                              {statusLabel}
                            </Box>
                          </AppTooltip>
                        ) : (
                          <OverflowTooltip
                            title={statusLabel}
                            placement="top"
                            className="status-badge"
                            sx={{ maxWidth: '100px' }}
                          >
                            {statusLabel}
                          </OverflowTooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default DayPlanTable;
